'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { SearchX } from 'lucide-react';
import { canGoBack } from './BackButton';
import { cn } from '@/lib/utils';

interface NotFoundStateProps {
  /**
   * What was looked for, lower-case singular: "group", "post", "business".
   * Builds the default heading ("Group Not Found") and body.
   */
  entity: string;
  /** Overrides the default "<Entity> Not Found" heading. */
  title?: string;
  /** Overrides the default "The <entity> you're looking for…" body. */
  description?: string;
  /**
   * The page's parent, used when there is nothing to go back to (a pasted
   * link, a hard refresh): "Go back" then lands here instead of doing nothing.
   */
  backHref?: string;
  /** A second, forward-looking way out, e.g. { label: 'Browse groups', href: '/groups' }. */
  action?: { label: string; href: string };
  /** Full-page states use the default; embed in a card with a smaller one. */
  className?: string;
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

// The one "doesn't exist / was removed" screen for every entity page. Before
// this each page had its own — different copy, no icon, some a bare <div> —
// so the same situation looked like a different bug on every page.
export default function NotFoundState({
  entity,
  title,
  description,
  backHref,
  action,
  className,
}: NotFoundStateProps) {
  const router = useRouter();

  const goBack = () => {
    if (canGoBack()) {
      router.back();
    } else if (backHref) {
      router.replace(backHref);
    } else {
      router.replace('/forums?tab=posts');
    }
  };

  return (
    <div
      role="status"
      className={cn(
        'flex min-h-[60vh] flex-col items-center justify-center px-6 text-center font-[Merriweather]',
        className
      )}
    >
      <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-[#cdc4c1]/40">
        <SearchX
          className="h-10 w-10 text-[#948780]"
          strokeWidth={1.75}
          aria-hidden="true"
        />
      </div>
      <h1 className="mb-2 text-2xl font-bold text-black sm:text-3xl">
        {title ?? `${capitalize(entity)} Not Found`}
      </h1>
      <p className="mb-6 max-w-md text-base text-[#554E49]">
        {description ??
          `The ${entity} you're looking for doesn't exist or has been removed.`}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={goBack}
          className="rounded-full bg-[#948780] px-6 py-2.5 font-semibold text-white shadow transition-colors hover:bg-[#7d726a] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          Go back
        </button>
        {action && (
          <Link
            href={action.href}
            className="rounded-full border border-[#cdc4c1] px-6 py-2.5 font-semibold text-black transition-colors hover:bg-[#cdc4c1]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {action.label}
          </Link>
        )}
      </div>
    </div>
  );
}
